import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { components } from 'react-select';
import { useTheme } from 'styled-components';
import ThemedSelect from 'components/ThemedSelect';
import { Icon, inkA, orange, tokens } from 'components/v3';

// The Forms pages' dropdown (Pit Pay: "everything should be custom"): the
// admin's ThemedSelect (react-select, so it's drawn by us and works from
// the keyboard: type to filter, arrows, Enter, Esc) restyled with the v3
// tokens for dark and light. The menu renders in a portal above modals and
// drawers. Options are { label, value, isDisabled? } or groups
// { label, options }; `value` and onChange use the raw option value.

const ERROR_RED = '#ED4337';
const MENU_Z = 10000; // above FormModal (9999) and the v3 Drawer (1000)

// Every option, with groups opened up.
export const flatOptions = (options) =>
  (options || []).reduce(
    (all, o) => all.concat(o && Array.isArray(o.options) ? o.options : [o]),
    []
  );

// The option holding `value` (compared as text), or null.
export const optionFor = (options, value) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    flatOptions(options).find((o) => String(o.value) === String(value)) || null
  );
};

// react-select styles from the v3 tokens.
export const v3SelectStyles = (theme, { error, icon } = {}) => {
  const t = tokens(theme);
  const ring = (focused) => {
    if (error) return ERROR_RED;
    return focused ? t.primary : t.fieldBorder;
  };
  const optionBg = (state) => {
    if (state.isSelected) return orange(0.16);
    if (state.isFocused && !state.isDisabled) return orange(0.08);
    return 'transparent';
  };
  const optionColor = (state) => {
    if (state.isDisabled) return t.faint;
    return state.isSelected ? t.primary : t.text;
  };
  return {
    container: (base) => ({ ...base, width: '100%' }),
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 10,
      backgroundColor: inkA(theme, state.isDisabled ? 0.02 : 0.04),
      borderColor: ring(state.isFocused),
      boxShadow: state.isFocused ? `0 0 0 3px ${orange(0.18)}` : 'none',
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isFocused || error ? ring(true) : inkA(theme, 0.24),
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: icon ? '2px 8px 2px 34px' : '2px 12px',
    }),
    singleValue: (base) => ({
      ...base,
      color: t.text,
      fontSize: 14,
      fontWeight: 600,
    }),
    placeholder: (base) => ({ ...base, color: t.faint, fontSize: 14 }),
    input: (base) => ({ ...base, color: t.text, fontSize: 14 }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: '8px 12px',
      color: state.isFocused ? t.primary : t.faint,
      '&:hover': { color: t.primary },
    }),
    menu: (base) => ({
      ...base,
      marginTop: 6,
      backgroundColor: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: 10,
      boxShadow: t.shadowPop,
      overflow: 'hidden',
    }),
    menuList: (base) => ({ ...base, padding: 4 }),
    menuPortal: (base) => ({ ...base, zIndex: MENU_Z }),
    group: (base) => ({ ...base, padding: '4px 0' }),
    groupHeading: (base) => ({
      ...base,
      margin: 0,
      padding: '6px 10px',
      color: t.faint,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: 6,
      padding: '9px 10px',
      fontSize: 14,
      fontWeight: state.isSelected ? 600 : 400,
      backgroundColor: optionBg(state),
      color: optionColor(state),
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:active': { backgroundColor: orange(0.16) },
    }),
    noOptionsMessage: (base) => ({ ...base, color: t.faint, fontSize: 13 }),
  };
};

// react-select's input with the error wiring (react-select 3 passes only
// aria-label / aria-labelledby through itself).
const AriaInput = (props) => {
  const { selectProps } = props;
  return (
    <components.Input
      {...props}
      aria-invalid={selectProps.ariaInvalid}
      aria-describedby={selectProps.ariaDescribedBy}
    />
  );
};

AriaInput.propTypes = { selectProps: PropTypes.object.isRequired };

const SELECT_COMPONENTS = { Input: AriaInput };

const FormsSelect = ({
  id,
  label,
  required,
  ariaLabel,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  error,
  icon,
  searchable,
  dataTour,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const [menuOpen, setMenuOpen] = useState(false);
  // Esc closes just the menu, not the modal or drawer around it (they
  // listen on the document).
  const onKeyDown = (e) => {
    if (e.key !== 'Escape' || !menuOpen) return;
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };
  return (
    <div data-tour={dataTour} style={{ width: '100%', minWidth: 0 }}>
      {label ? (
        <label
          htmlFor={id}
          style={{
            display: 'block',
            marginBottom: 7,
            fontSize: 13,
            fontWeight: 600,
            color: t.sub,
          }}
        >
          {label}
          {required ? (
            <span aria-hidden="true" style={{ color: ERROR_RED }}>
              {' *'}
            </span>
          ) : null}
        </label>
      ) : null}
      <div style={{ position: 'relative' }}>
        {icon ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              display: 'inline-flex',
              color: t.faint,
              pointerEvents: 'none',
            }}
          >
            <Icon name={icon} size={14} color="currentColor" />
          </span>
        ) : null}
        <ThemedSelect
          inputId={id}
          aria-label={label ? undefined : ariaLabel}
          ariaInvalid={error ? 'true' : undefined}
          ariaDescribedBy={error ? `${id}-error` : undefined}
          components={SELECT_COMPONENTS}
          options={options}
          value={optionFor(options, value)}
          onChange={(option) => onChange(option ? option.value : null)}
          onKeyDown={onKeyDown}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          placeholder={placeholder}
          isDisabled={disabled}
          isSearchable={searchable}
          classNamePrefix="fb-select"
          noOptionsMessage={() => 'No matches'}
          menuPlacement="auto"
          menuPortalTarget={document.body}
          customStyles={v3SelectStyles(theme, { error: !!error, icon })}
        />
      </div>
      {error ? (
        <div
          id={`${id}-error`}
          role="alert"
          style={{
            marginTop: 6,
            fontSize: 12.5,
            fontWeight: 600,
            color: ERROR_RED,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
};

const optionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDisabled: PropTypes.bool,
});

FormsSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  ariaLabel: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      optionShape,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(optionShape).isRequired,
      }),
    ])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  icon: PropTypes.string,
  searchable: PropTypes.bool,
  dataTour: PropTypes.string,
};

FormsSelect.defaultProps = {
  label: undefined,
  required: false,
  ariaLabel: undefined,
  value: null,
  placeholder: 'Select…',
  disabled: false,
  error: null,
  icon: undefined,
  searchable: true,
  dataTour: undefined,
};

export default FormsSelect;
